import React from 'react';
import { SectionTitle } from './SectionTitle';
import { ScrollAnimation } from './ScrollAnimation';
import { SpotlightCard } from './SpotlightCard';
import { LightBulbIcon, ExclamationTriangleIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '../i18n/LanguageContext';

interface ManifestoItemProps {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  content: string;
}

const icons = {
    belief: LightBulbIcon,
    problem: ExclamationTriangleIcon,
    commitment: CheckBadgeIcon
};

const ManifestoCard: React.FC<ManifestoItemProps> = ({ icon: Icon, title, content }) => (
  <SpotlightCard className="bg-brand-primary/60 p-6 rounded-lg shadow-xl border border-slate-700/50 hover:border-brand-accent/40 transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col">
    <div className="flex items-center mb-4">
      <div className="w-12 h-12 bg-brand-primary rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
        <Icon className="w-7 h-7 text-brand-accent" />
      </div>
      <h3 className="text-xl sm:text-2xl font-serif font-semibold text-brand-light">{title}</h3>
    </div>
    <p className="text-brand-text text-sm sm:text-base flex-grow" dangerouslySetInnerHTML={{ __html: content }} />
  </SpotlightCard>
);

export const ManifestoSection: React.FC = () => {
  const { t } = useTranslation();
  const manifestoItems = t('manifesto.items') as { key: keyof typeof icons; title: string; content: string }[];

  return (
    <section id="manifesto" className="py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation animation="slideInUp">
          <SectionTitle
            title={t('manifesto.title')}
            subtitle={t('manifesto.subtitle')}
          />
        </ScrollAnimation>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {manifestoItems.map((item, index) => {
            const Icon = icons[item.key];
            return (
              <ScrollAnimation key={index} animation="slideInUp" delay={150 + index * 100}>
                <ManifestoCard icon={Icon} title={item.title} content={item.content} />
              </ScrollAnimation>
            );
          })}
        </div>
        <ScrollAnimation animation="fadeIn" delay={500}>
          <p className="text-center mt-12 text-lg text-brand-text max-w-3xl mx-auto"
             dangerouslySetInnerHTML={{ __html: t('manifesto.conclusion') }}
          />
        </ScrollAnimation>
      </div>
    </section>
  );
};